import { useEffect, useState } from 'react';

import { tokenFormatter } from '@/lib/formatting';

import {
  ToastContainer,
  ToastContent,
  ToastTitle,
  useAutoDismiss,
} from '@/components/TransactionToast/ToastComponents';

import { getSettlementTime, NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { isNotObservedReceipt, type Transaction } from '@/store/transaction/types';
import {
  isTransactionPending,
  isTransactionSuccessful,
  getTxHash,
} from '@/store/transaction/utils';

import { fontStyles } from '@/styles/typography';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';

import { formatShortAddress } from '@/utils/address';
import { classNames } from '@/utils/classnames';

import { FeedbackAnimation } from '../animations/FeedbackAnimation';
import { TokenSwapAnimation } from '../animations/TokenSwapAnimation';
import { TradeToastDetailsLink } from './TradeToastDetailsLink';

type TradeToastSwapProps = {
  transaction: Transaction;
  onDismiss: () => void;
  onHeightChange: () => void;
};

export const TradeToastSwap = ({ transaction, onDismiss, onHeightChange }: TradeToastSwapProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [slowTransactionWarning, setSlowTransactionWarning] = useState(false);

  const {
    sellToken,
    buyToken,
    buyAmount,
    sellAmount,
    recipient,
    hash: tradeHash,
    transactionType,
  } = transaction;
  const txHash = getTxHash(transaction) ?? tradeHash;

  const isGasless = transactionType === 'intents';
  const isPending = isTransactionPending(transaction);
  const isSuccess = isTransactionSuccessful(transaction);
  const isError = !isPending && !isSuccess;

  const isNotObserved = isNotObservedReceipt(transaction.raw);
  const feedback = isSuccess ? 'success' : isError ? 'error' : undefined;

  useAutoDismiss(isHovered, onDismiss, isPending);

  useEffect(() => {
    if (!isPending || isGasless) {
      setSlowTransactionWarning(false);
      return;
    }

    // Same p95 threshold as the pending screen's "taking longer than usual" copy.
    const { slow } = getSettlementTime(sellToken.chainId, 'standard');
    const timer = setTimeout(() => setSlowTransactionWarning(true), slow * 1000);

    return () => {
      clearTimeout(timer);
      setSlowTransactionWarning(false);
    };
  }, [isGasless, isPending, sellToken.chainId]);

  const chainName = NETWORK_NAME_PER_CHAIN_ID[sellToken.chainId];
  const showDetailsLink = !isPending && (!isGasless || isSuccess) && !isNotObserved;

  const buyAmountFormatted = tokenFormatter.format(buyAmount, { decimals: buyToken.decimals });
  const sellAmountFormatted = tokenFormatter.format(sellAmount, { decimals: sellToken.decimals });

  const errorTitle = isNotObserved
    ? 'Transaction incomplete, check your wallet.'
    : `${isGasless ? 'Submission' : 'Transaction'} failed`;
  const title = isPending
    ? 'Transaction pending...'
    : isSuccess
      ? 'Transaction successful'
      : errorTitle;

  return (
    <ToastContainer
      onClose={onDismiss}
      onHeightChange={onHeightChange}
      showProgressAnimation={isPending}
      onHoverChange={setIsHovered}
    >
      <FeedbackAnimation feedback={feedback} className="relative z-10 shrink-0">
        <TokenSwapAnimation fromToken={sellToken} toToken={buyToken} isComplete={!isPending} />
      </FeedbackAnimation>
      <div className="relative z-10 flex min-w-0 flex-1 flex-col gap-1">
        <ToastTitle>{title}</ToastTitle>
        <ToastContent>
          <div
            className={classNames(
              'flex items-center gap-1',
              isSuccess ? 'flex-wrap' : 'whitespace-nowrap',
            )}
          >
            <span
              className={fontStyles({
                variant: 'LABEL_MD',
                class: ['text-token-gray-1600 min-w-0 block', !isSuccess ? 'truncate' : undefined],
              })}
            >
              {isSuccess
                ? `Received ${buyAmountFormatted} ${buyToken.symbol}`
                : `${sellAmountFormatted} ${sellToken.symbol} to ${buyAmountFormatted} ${buyToken.symbol}`}
            </span>
            <div className="flex items-center">
              <span className={fontStyles({ variant: 'LABEL_MD', class: 'text-token-gray-700' })}>
                on&nbsp;
              </span>
              <div className="flex items-center shrink-0">
                <ChainAvatar
                  chainId={sellToken.chainId}
                  alt={chainName}
                  size={16}
                  className="mr-1"
                />
                <span
                  className={fontStyles({
                    variant: 'LABEL_MD',
                    class: 'text-token-gray-700 whitespace-nowrap',
                  })}
                >
                  {chainName}
                </span>
              </div>
            </div>
          </div>
        </ToastContent>
        {isPending && slowTransactionWarning && (
          <ToastContent>
            <span className={fontStyles({ variant: 'LABEL_SM', class: 'text-token-gray-700' })}>
              This is taking longer than usual. Consider speeding up the transaction in your wallet.
            </span>
          </ToastContent>
        )}
        {showDetailsLink && (
          <div className="mt-1">
            {recipient && isSuccess && (
              <ToastContent>
                <span className={fontStyles({ variant: 'LABEL_MD', class: 'text-token-gray-700' })}>
                  Transferred to {formatShortAddress(recipient)}
                </span>
              </ToastContent>
            )}
            <TradeToastDetailsLink
              chainId={sellToken.chainId}
              hash={txHash}
              onDismiss={onDismiss}
              useBlockExplorer={isError}
            />
          </div>
        )}
      </div>
    </ToastContainer>
  );
};
